import { type FC, Fragment, Suspense, lazy } from 'react';
import { graphql, useFragment } from 'react-relay';

/* Helpers */
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import { useSbSelector } from '../../reducers/useSbSelector';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy';

import { useMergedSbAndSponsoredItems } from '../hooks/useMergedSbAndSponsoredItems';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

/* Components */
import { SbSharedProductAdWrapper } from '../SbSharedProductAdWrapper/SbSharedProductAdWrapper';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { QuickViewProvider } from 'dibs-search-product-tile/exports/QuickViewProvider';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { useMeasurementUnit } from '../useMeasurementUnit';
import { useSbSharedItemTracking } from '../hooks/sharedItemTracking/useSbSharedItemTracking';
import { useSharedUrgencySignals } from '../hooks/sharedUrgencySignals/useSharedUrgencySignals';
import { useItemShippingPrices } from '../hooks/useItemShippingPrices';
import { usePartialItems } from '../hooks/usePartialItems';
import { SbRespPlaceholderTile } from './SbRespPlaceholderTile';
import { SbSharedPartialItemsPlaceholder } from '../SbSharedPartialItemsPlaceholder/SbSharedPartialItemsPlaceholder';

/* Constants */
import { VERTICAL_OFFSET_SB_RESP_IMG } from '../../constants/imageConstants';

import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps';
import { type SbRespSearchResultContainer_viewer$key } from './__generated__/SbRespSearchResultContainer_viewer.graphql';
import { type SbRespSearchResultContainer_itemSearch$key } from './__generated__/SbRespSearchResultContainer_itemSearch.graphql';
import { type SbRespSearchResultContainer_user$key } from './__generated__/SbRespSearchResultContainer_user.graphql';
import { type SbRespSearchResultContainer_item$key } from './__generated__/SbRespSearchResultContainer_item.graphql';

import styles from './sbRespSearchResultContainer.scss';

const MerchandizingTool = lazy(
    () =>
        import(
            /* webpackChunkName: "MerchandizingTool" */ 'dibs-merchandizing-tools/exports/MerchandizingTool'
        )
);

const viewerFragment = graphql`
    fragment SbRespSearchResultContainer_viewer on Viewer {
        ...SharedItemTile_viewer
        ...useSbSharedItemTracking_viewer
    }
`;
const itemSearchFragment = graphql`
    fragment SbRespSearchResultContainer_itemSearch on ItemSearchQueryConnection {
        ...useMeasurementUnit_itemSearch
        ...useSbSharedItemTracking_itemSearch
        ...SbSharedProductAdWrapper_itemSearch
        ...SharedItemTile_itemSearch
        ...useMergedSbAndSponsoredItems_itemSearch
        ...SbSharedPartialItemsPlaceholder_itemSearch
    }
`;
const userFragment = graphql`
    fragment SbRespSearchResultContainer_user on User {
        serviceId
        hasMerchandizingPermission: hasPermission(name: "BUYER_FE_MERCH_TOOL")
        ...useMeasurementUnit_user
        ...SharedItemTile_user
    }
`;

const mergedItemsFragment = graphql`
    fragment SbRespSearchResultContainer_item on Item @relay(plural: true) {
        serviceId
        isSold
        ...usePartialItems_item
        ...FavoritesProvider_item
        ...useSharedUrgencySignals_item
        ...useItemShippingPrices_item
        ...useSbSharedItemTracking_item
        ...SharedItemTile_item
            @arguments(
                isTrade: $isTrade
                fetchVideo: $fetchTileVideo
                priceBookName: $priceBookName
                showSeller: $showSeller
            )
    }
`;

const ITEMS_PER_ROW = 3;
const LAZY_LOAD_ITEM_TILE_INDEX = ITEMS_PER_ROW * 2;

type Props = {
    viewer: SbRespSearchResultContainer_viewer$key;
    itemSearch: SbRespSearchResultContainer_itemSearch$key;
    user: SbRespSearchResultContainer_user$key;
    showMeasurements: boolean;
    showInternalSellerName?: boolean;
    showSellerName?: boolean;
    showCreatorName?: boolean;
    showStorefrontOnlyBadge?: boolean;
};

export const SbRespSearchResultContainer: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    user: userRef,
    showMeasurements,
    showInternalSellerName,
    showSellerName,
    showCreatorName,
    showStorefrontOnlyBadge,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const user = useFragment(userFragment, userRef);

    const { first: pageSize = 60, isClient } = useSbSelector(
        state => state.relayVariables.variables
    );
    const refetchInFlight = useSbSelector(state => state.filters.refetchInFlight);
    const [measurementUnit] = useMeasurementUnit({ itemSearch, user });
    const { isItemTileRedesignTestVariant, isItemFavoritingVariant } = useServerVarsContext();

    const userId = user?.serviceId || '';

    const { mergedItems: mergedItemsRef, sponsoredItemsIndexMetadataMap } =
        useMergedSbAndSponsoredItems({ itemSearch });

    const mergedItems = useFragment<SbRespSearchResultContainer_item$key>(
        mergedItemsFragment,
        mergedItemsRef
    );

    const { fetchPartialItems, partialItemsIds, numOfInflatedItems, numOfPlaceholderItems } =
        usePartialItems(mergedItems);
    const inflatedItems = partialItemsIds.length
        ? mergedItems.slice(0, numOfInflatedItems)
        : mergedItems;

    const itemIds = [...new Set(mergedItems.map(item => item?.serviceId))].filter(filterFalsy);

    const { fetchItemUrgencySignals, getUrgencySignal } = useSharedUrgencySignals(mergedItems);
    const { fetchItemShippingPrices, getItemShippingPrices, getItemShippingTrackingData } =
        useItemShippingPrices(mergedItems);
    const { fireItemImpressionTracking, fireItemClickTracking } = useSbSharedItemTracking({
        viewer,
        itemSearch,
        items: mergedItems,
        getUrgencySignal,
        getItemShippingPrices,
        getItemShippingTrackingData,
    });

    return (
        <div
            className={isItemTileRedesignTestVariant ? styles.gridContainer : styles.container}
            data-tn="search-results-container"
        >
            {refetchInFlight ? (
                Array.from(Array(pageSize)).map((_, index) => <SbRespPlaceholderTile key={index} />)
            ) : (
                <FavoritesProvider
                    disable={!isClient}
                    items={mergedItems}
                    location={locations.SEARCH_BROWSE}
                    userId={userId}
                >
                    {({ props: favorites }: { props: FavoritesProviderChildrenProps }) => (
                        <QuickViewProvider>
                            <>
                                {inflatedItems.map((item, index) => {
                                    const isSponsored =
                                        sponsoredItemsIndexMetadataMap &&
                                        typeof sponsoredItemsIndexMetadataMap[index] !==
                                            'undefined';

                                    const itemKey = item?.serviceId
                                        ? `${item?.serviceId}${isSponsored ? '_SPONSORED' : ''}`
                                        : index;

                                    if (refetchInFlight) {
                                        return <SbRespPlaceholderTile key={itemKey} />;
                                    }
                                    return (
                                        <Fragment key={itemKey}>
                                            <SbSharedProductAdWrapper
                                                //finding original item index
                                                index={index}
                                                itemSearch={itemSearch}
                                            />

                                            <SharedItemTile
                                                isSponsored={isSponsored}
                                                isVisuallySimilarEnabled
                                                item={item}
                                                user={user}
                                                favorites={favorites}
                                                index={index}
                                                showInternalSellerName={showInternalSellerName}
                                                showStorefrontOnlyBadge={showStorefrontOnlyBadge}
                                                showSellerName={showSellerName}
                                                showCreatorName={showCreatorName}
                                                showMeasurements={showMeasurements}
                                                lazyLoadStartIndex={LAZY_LOAD_ITEM_TILE_INDEX}
                                                imageLoadVerticalOffset={
                                                    VERTICAL_OFFSET_SB_RESP_IMG
                                                }
                                                beforeRender={() => {
                                                    fetchItemUrgencySignals(index);
                                                    fetchItemShippingPrices(index);
                                                }}
                                                onContentVisible={() => {
                                                    fireItemImpressionTracking({
                                                        itemId: item?.serviceId,
                                                        index,
                                                        isSponsored,
                                                    });
                                                }}
                                                onClick={() =>
                                                    fireItemClickTracking({
                                                        itemId: item?.serviceId,
                                                        index,
                                                        isSponsored,
                                                    })
                                                }
                                                measurementUnit={measurementUnit}
                                                viewer={viewer}
                                                itemSearch={itemSearch}
                                                fetchFolderOnMouseOver
                                                showFavoritesTooltip={
                                                    index === 0 && !isItemFavoritingVariant
                                                }
                                                additionalElements={
                                                    user?.hasMerchandizingPermission && (
                                                        <div
                                                            data-dibs-merchandizing-tool-item={
                                                                item?.serviceId
                                                            }
                                                        />
                                                    )
                                                }
                                                hideRecentSoldPrice={!user && item?.isSold}
                                                urgencySignal={
                                                    getUrgencySignal(item.serviceId)?.message
                                                }
                                                shippingPrices={getItemShippingPrices(
                                                    item.serviceId
                                                )}
                                            />
                                        </Fragment>
                                    );
                                })}
                                <SbSharedPartialItemsPlaceholder
                                    itemSearch={itemSearch}
                                    numOfPlaceholderItems={numOfPlaceholderItems}
                                    fetchPartialItems={fetchPartialItems}
                                    numOfInflatedItems={numOfInflatedItems}
                                />
                                {user?.hasMerchandizingPermission && (
                                    <Suspense fallback={null}>
                                        <MerchandizingTool
                                            itemIds={itemIds}
                                            userId={userId}
                                            placement="sb"
                                            refetchInFlight={refetchInFlight}
                                            isItemTileRedesignTestVariant={
                                                isItemTileRedesignTestVariant
                                            }
                                        />
                                    </Suspense>
                                )}
                            </>
                        </QuickViewProvider>
                    )}
                </FavoritesProvider>
            )}
        </div>
    );
};
